import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowDownAZ,
  ArrowUp,
  ArrowUpAZ,
  Bug,
  Calendar,
  CheckSquare,
  CircleDashed,
  CircleDot,
  FileText,
  Filter,
  Flame,
  GitBranch,
  Hash,
  ListFilter,
  ListTodo,
  Minus,
  Plus,
  Search,
  SlidersHorizontal,
  Sparkles,
  User,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useOutletContext } from 'react-router-dom';

import { useIssueDrawerStore } from '@/stores/issue-drawer-store';

import { EmptyState } from '@/components/common/empty-state';
import type { ProjectLayoutContext } from '@/components/layout/project-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { extractApiErrorMessage } from '@/lib/api';
import {
  createIssue,
  getProjectByKey,
  listIssues,
  listStatuses,
  type IssuePriority,
  type IssueSummary,
  type IssueType,
} from '@/lib/project-api';
import { useAuthStore } from '@/stores/auth-store';

const TYPE_OPTIONS: IssueType[] = ['STORY', 'TASK', 'BUG', 'SUBTASK'];
const PRIORITY_OPTIONS: IssuePriority[] = ['LOWEST', 'LOW', 'MEDIUM', 'HIGH', 'HIGHEST'];

const TYPE_META: Record<
  IssueType,
  { label: string; icon: typeof FileText; iconColor: string; bg: string }
> = {
  STORY: {
    label: 'Story',
    icon: FileText,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-500/10 ring-emerald-500/20',
  },
  TASK: {
    label: 'Task',
    icon: CheckSquare,
    iconColor: 'text-sky-600 dark:text-sky-400',
    bg: 'bg-sky-500/10 ring-sky-500/20',
  },
  BUG: {
    label: 'Bug',
    icon: Bug,
    iconColor: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-500/10 ring-rose-500/20',
  },
  SUBTASK: {
    label: 'Subtask',
    icon: CircleDot,
    iconColor: 'text-slate-500 dark:text-slate-400',
    bg: 'bg-muted ring-border',
  },
};

const PRIORITY_META: Record<
  IssuePriority,
  { label: string; icon: typeof ArrowUp; color: string }
> = {
  LOWEST: { label: 'Lowest', icon: ArrowDown, color: 'text-slate-400' },
  LOW: { label: 'Low', icon: ArrowDown, color: 'text-emerald-500' },
  MEDIUM: { label: 'Medium', icon: Minus, color: 'text-amber-500' },
  HIGH: { label: 'High', icon: ArrowUp, color: 'text-orange-500' },
  HIGHEST: { label: 'Highest', icon: Flame, color: 'text-rose-500' },
};

const STATUS_CATEGORY_CLASS: Record<IssueSummary['statusCategory'], string> = {
  TODO: 'bg-slate-500/10 text-slate-700 ring-slate-500/30 dark:text-slate-300',
  IN_PROGRESS: 'bg-sky-500/10 text-sky-700 ring-sky-500/30 dark:text-sky-300',
  DONE: 'bg-emerald-500/10 text-emerald-700 ring-emerald-500/30 dark:text-emerald-300',
};

const PAGE_SIZE = 25;

export function ProjectIssuesPage() {
  const { orgId, orgSlug, projectKey } = useOutletContext<ProjectLayoutContext>();
  const openIssue = useIssueDrawerStore((s) => s.open);
  const user = useAuthStore((s) => s.user);
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const membership = user?.memberships.find((m) => m.organizationId === activeOrgId);
  const canCreate = Boolean(user?.isSuperAdmin || membership?.permissions.includes('issue.create'));

  const projectQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey],
    queryFn: () => getProjectByKey(orgId, projectKey),
  });

  const statusesQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey, 'statuses'],
    queryFn: () => {
      if (!projectQuery.data) return Promise.resolve([]);
      return listStatuses(orgId, projectQuery.data.id);
    },
    enabled: Boolean(projectQuery.data),
  });

  const [types, setTypes] = useState<IssueType[]>([]);
  const [statusIds, setStatusIds] = useState<string[]>([]);
  const [hasParent, setHasParent] = useState<'ALL' | 'YES' | 'NO'>('ALL');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<'updatedAt' | 'createdAt' | 'priority' | 'storyPoints'>(
    'updatedAt',
  );
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debounced, types.length, statusIds.length, hasParent, sort, order]);

  const issuesQuery = useQuery({
    queryKey: [
      'org',
      orgId,
      'project',
      projectKey,
      'issues',
      { types, statusIds, hasParent, debounced, sort, order, page },
    ],
    queryFn: () => {
      if (!projectQuery.data)
        return Promise.resolve({ items: [], total: 0, limit: PAGE_SIZE, offset: 0 });
      return listIssues(orgId, projectQuery.data.id, {
        ...(types.length ? { type: types } : {}),
        ...(statusIds.length ? { statusId: statusIds } : {}),
        ...(hasParent !== 'ALL' ? { hasParent: hasParent === 'YES' } : {}),
        ...(debounced ? { searchText: debounced } : {}),
        sort,
        order,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
    },
    enabled: Boolean(projectQuery.data),
    placeholderData: (prev) => prev,
  });

  const total = issuesQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const empty = !issuesQuery.isLoading && (issuesQuery.data?.items.length ?? 0) === 0;

  const anyFiltersActive =
    Boolean(debounced) || types.length > 0 || statusIds.length > 0 || hasParent !== 'ALL';

  const clearFilters = () => {
    setSearch('');
    setTypes([]);
    setStatusIds([]);
    setHasParent('ALL');
  };

  const toggleType = (t: IssueType) => {
    setTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const toggleStatus = (id: string) => {
    setStatusIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 p-6">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
        <div
          className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/10 blur-3xl"
          aria-hidden
        />
        <div className="relative flex flex-wrap items-start justify-between gap-4 p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <ListTodo className="h-5 w-5" aria-hidden />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Issues</h1>
              <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <span>Every issue in</span>
                <span className="inline-flex items-center gap-1 rounded-md bg-background/80 px-2 py-0.5 font-mono text-xs font-medium ring-1 ring-border">
                  {projectKey}
                </span>
                <span className="inline-flex items-center gap-1 rounded-md bg-background/80 px-2 py-0.5 text-xs font-medium ring-1 ring-border tabular-nums">
                  <Hash className="h-3 w-3" aria-hidden />
                  {total} total
                </span>
              </p>
            </div>
          </div>
          {canCreate ? (
            <Button onClick={() => setCreateOpen(true)} disabled={!projectQuery.data} className="gap-1.5">
              <Plus className="h-4 w-4" aria-hidden /> New issue
            </Button>
          ) : null}
        </div>
      </div>

      {/* Filters */}
      <Card className="overflow-hidden">
        <CardContent className="space-y-4 p-4">
          {/* Top row: search + parent + sort */}
          <div className="grid gap-3 md:grid-cols-12">
            <div className="md:col-span-6">
              <Label
                htmlFor="issue-search"
                className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <Search className="h-3 w-3" aria-hidden /> Search
              </Label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  id="issue-search"
                  className="h-10 pl-9 pr-9 text-sm"
                  placeholder="Title, key, or description…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search ? (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                  </button>
                ) : null}
              </div>
            </div>

            <div className="md:col-span-3">
              <Label
                htmlFor="issue-parent"
                className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <GitBranch className="h-3 w-3" aria-hidden /> Hierarchy
              </Label>
              <Select
                id="issue-parent"
                className="h-10 text-sm"
                value={hasParent}
                onChange={(e) => setHasParent(e.target.value as typeof hasParent)}
              >
                <option value="ALL">All issues</option>
                <option value="NO">Top-level only</option>
                <option value="YES">Subtasks only</option>
              </Select>
            </div>

            <div className="md:col-span-3">
              <Label
                htmlFor="issue-sort"
                className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <SlidersHorizontal className="h-3 w-3" aria-hidden /> Sort
              </Label>
              <div className="flex items-stretch gap-1.5">
                <Select
                  id="issue-sort"
                  wrapperClassName="flex-1"
                  className="text-sm"
                  value={sort}
                  onChange={(e) => setSort(e.target.value as typeof sort)}
                >
                  <option value="updatedAt">Updated</option>
                  <option value="createdAt">Created</option>
                  <option value="priority">Priority</option>
                  <option value="storyPoints">Story points</option>
                </Select>
                <button
                  type="button"
                  onClick={() => setOrder(order === 'desc' ? 'asc' : 'desc')}
                  aria-label={`Sort ${order === 'desc' ? 'descending' : 'ascending'}`}
                  title={`Sort ${order === 'desc' ? 'descending' : 'ascending'}`}
                  className="flex h-10 w-10 items-center justify-center rounded-md border border-input bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  {order === 'desc' ? (
                    <ArrowDownAZ className="h-4 w-4" aria-hidden />
                  ) : (
                    <ArrowUpAZ className="h-4 w-4" aria-hidden />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Type chips */}
          <div>
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <Filter className="h-3 w-3" aria-hidden /> Types
            </div>
            <div className="flex flex-wrap gap-2">
              {TYPE_OPTIONS.map((t) => {
                const meta = TYPE_META[t];
                const Icon = meta.icon;
                const selected = types.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleType(t)}
                    aria-pressed={selected}
                    className={[
                      'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                      selected
                        ? `${meta.bg} ring-2 ring-offset-0`
                        : 'border-input text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                    ].join(' ')}
                  >
                    <Icon
                      className={[
                        'h-3.5 w-3.5',
                        selected ? meta.iconColor : 'text-muted-foreground',
                      ].join(' ')}
                      aria-hidden
                    />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Status chips */}
          {(statusesQuery.data ?? []).length > 0 ? (
            <div>
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <ListFilter className="h-3 w-3" aria-hidden /> Statuses
              </div>
              <div className="flex flex-wrap gap-2">
                {(statusesQuery.data ?? []).map((s) => {
                  const selected = statusIds.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleStatus(s.id)}
                      aria-pressed={selected}
                      style={selected ? { boxShadow: `inset 0 0 0 2px ${s.colorHex}` } : undefined}
                      className={[
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                        selected
                          ? 'border-transparent bg-background text-foreground'
                          : 'border-input text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                      ].join(' ')}
                    >
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: s.colorHex }}
                        aria-hidden
                      />
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {anyFiltersActive ? (
            <div className="flex items-center justify-between border-t pt-3 text-xs text-muted-foreground">
              <span>Filters active</span>
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium text-foreground transition-colors hover:bg-muted"
              >
                <X className="h-3 w-3" aria-hidden /> Clear all
              </button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {issuesQuery.isError ? (
        <EmptyState
          title="Could not load issues"
          description={extractApiErrorMessage(issuesQuery.error)}
          action={<Button onClick={() => issuesQuery.refetch()}>Retry</Button>}
        />
      ) : empty ? (
        <div className="rounded-xl border border-dashed bg-muted/20 p-12">
          <div className="mx-auto flex max-w-md flex-col items-center gap-3 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <ListTodo className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <h3 className="text-base font-semibold">
                {anyFiltersActive ? 'No matching issues' : 'No issues yet'}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {anyFiltersActive
                  ? 'Try adjusting or clearing the filters to see more results.'
                  : canCreate
                    ? 'Create the first issue to start tracking work.'
                    : 'Ask a project lead to create the first issue.'}
              </p>
            </div>
            {anyFiltersActive ? (
              <Button variant="outline" onClick={clearFilters} className="gap-1.5">
                <X className="h-4 w-4" aria-hidden /> Clear filters
              </Button>
            ) : canCreate ? (
              <Button onClick={() => setCreateOpen(true)} className="gap-1.5">
                <Plus className="h-4 w-4" aria-hidden /> New issue
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="w-[46px]"> </TableHead>
                  <TableHead className="w-[120px]">Key</TableHead>
                  <TableHead>Summary</TableHead>
                  <TableHead className="w-[44px] text-center">
                    <Flame className="mx-auto h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assignee</TableHead>
                  <TableHead className="w-[64px] text-center">SP</TableHead>
                  <TableHead className="w-[120px] text-right">Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {issuesQuery.isLoading
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <TableRow key={`sk-${i}`}>
                        {Array.from({ length: 8 }).map((__, j) => (
                          <TableCell key={`sk-${i}-${j}`}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : issuesQuery.data?.items.map((issue) => {
                      const typeMeta = TYPE_META[issue.type];
                      const TypeIconCmp = typeMeta.icon;
                      const priMeta = PRIORITY_META[issue.priority];
                      const PriIconCmp = priMeta.icon;
                      const assigneeInitial =
                        issue.assigneeName?.trim()?.charAt(0)?.toUpperCase() ?? '?';
                      const openDrawer = () =>
                        openIssue({
                          orgId,
                          orgSlug,
                          projectKey,
                          issueKey: issue.key,
                        });
                      return (
                        <TableRow
                          key={issue.id}
                          onClick={openDrawer}
                          className="group cursor-pointer transition-colors hover:bg-muted/40"
                        >
                          <TableCell>
                            <div
                              className={[
                                'flex h-7 w-7 items-center justify-center rounded-md ring-1',
                                typeMeta.bg,
                              ].join(' ')}
                              title={typeMeta.label}
                            >
                              <TypeIconCmp
                                className={['h-3.5 w-3.5', typeMeta.iconColor].join(' ')}
                                aria-hidden
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDrawer();
                              }}
                              className="font-mono text-xs font-medium text-primary hover:underline"
                            >
                              {issue.key}
                            </button>
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <span className="block truncate text-sm text-foreground group-hover:text-primary">
                              {issue.title}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <PriIconCmp
                              className={['mx-auto h-4 w-4', priMeta.color].join(' ')}
                              aria-label={`Priority ${priMeta.label}`}
                            />
                          </TableCell>
                          <TableCell>
                            <span
                              className={[
                                'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
                                STATUS_CATEGORY_CLASS[issue.statusCategory],
                              ].join(' ')}
                            >
                              {issue.statusName}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {issue.assigneeName ? (
                                <>
                                  <span
                                    className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold uppercase text-primary ring-1 ring-primary/20"
                                    title={issue.assigneeName}
                                    aria-hidden
                                  >
                                    {assigneeInitial}
                                  </span>
                                  <span className="truncate text-xs text-muted-foreground">
                                    {issue.assigneeName}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span
                                    className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-muted-foreground ring-1 ring-border"
                                    aria-hidden
                                  >
                                    <User className="h-3 w-3" aria-hidden />
                                  </span>
                                  <span className="text-xs italic text-muted-foreground">
                                    Unassigned
                                  </span>
                                </>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {issue.storyPoints != null ? (
                              <span className="inline-flex h-6 min-w-[1.75rem] items-center justify-center rounded-full bg-muted px-2 text-[11px] font-semibold tabular-nums ring-1 ring-border">
                                {issue.storyPoints}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                              <Calendar className="h-3 w-3" aria-hidden />
                              {new Date(issue.updatedAt).toLocaleDateString()}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-between rounded-lg border bg-muted/20 px-4 py-2 text-xs">
          <span className="text-muted-foreground">
            Page <span className="font-semibold text-foreground">{page + 1}</span> of{' '}
            <span className="font-semibold text-foreground">{totalPages}</span> ·{' '}
            <span className="font-semibold text-foreground">{total}</span> total
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <CreateIssueDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        orgId={orgId}
        projectId={projectQuery.data?.id ?? ''}
        projectKey={projectKey}
      />
    </div>
  );
}

function CreateIssueDialog({
  open,
  onOpenChange,
  orgId,
  projectId,
  projectKey,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  orgId: string;
  projectId: string;
  projectKey: string;
}) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [title, setTitle] = useState('');
  const [type, setType] = useState<IssueType>('TASK');
  const [priority, setPriority] = useState<IssuePriority>('MEDIUM');
  const [description, setDescription] = useState('');
  const [parentKey, setParentKey] = useState('');
  const [storyPoints, setStoryPoints] = useState('');

  const parentsQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey, 'potential-parents'],
    queryFn: () =>
      listIssues(orgId, projectId, {
        type: ['STORY', 'TASK', 'BUG'],
        limit: 50,
        sort: 'updatedAt',
      }),
    enabled: open && type === 'SUBTASK' && Boolean(projectId),
  });

  const parentOptions = useMemo(() => parentsQuery.data?.items ?? [], [parentsQuery.data]);

  useEffect(() => {
    if (!open) {
      setTitle('');
      setType('TASK');
      setPriority('MEDIUM');
      setDescription('');
      setParentKey('');
      setStoryPoints('');
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: async () => {
      const parentIssueId =
        type === 'SUBTASK' && parentKey
          ? (parentOptions.find((i) => i.id === parentKey)?.id ?? null)
          : null;
      const desc = description.trim();
      return createIssue(orgId, projectId, {
        title: title.trim(),
        type,
        priority,
        ...(desc ? { descriptionMarkdown: desc } : {}),
        ...(parentIssueId ? { parentIssueId } : {}),
        ...(storyPoints ? { storyPoints: Number(storyPoints) } : {}),
      });
    },
    onSuccess: (issue) => {
      success('Issue created', issue.key);
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'issues'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'recent-issues'] });
      onOpenChange(false);
    },
    onError: (e) => error('Could not create issue', extractApiErrorMessage(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || mutation.isPending) return;
    if (type === 'SUBTASK' && !parentKey) {
      error('Select a parent', 'Subtasks require a parent issue in the same project.');
      return;
    }
    mutation.mutate();
  };

  const bumpPoints = (delta: number) => {
    const current = storyPoints.trim() === '' ? 0 : Number(storyPoints);
    const safe = Number.isFinite(current) ? current : 0;
    const next = Math.min(999, Math.max(0, safe + delta));
    setStoryPoints(String(next));
  };

  const SelectedTypeIcon = TYPE_META[type].icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl overflow-hidden p-0">
        {/* Gradient header */}
        <div className="relative border-b bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pb-5 pt-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <Sparkles className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <DialogHeader>
                <DialogTitle className="text-xl">Create issue</DialogTitle>
                <DialogDescription className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 rounded-md bg-background/80 px-2 py-0.5 font-mono text-[11px] ring-1 ring-border">
                    {projectKey}-•••
                  </span>
                  <span>will be auto-generated.</span>
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        <form className="space-y-5 px-6 pb-6 pt-5" onSubmit={submit} noValidate>
          {/* Type chips */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Filter className="h-3.5 w-3.5" aria-hidden /> Type
            </Label>
            <div className="grid grid-cols-4 gap-2">
              {TYPE_OPTIONS.map((t) => {
                const meta = TYPE_META[t];
                const Icon = meta.icon;
                const selected = t === type;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    aria-pressed={selected}
                    className={[
                      'group flex items-center justify-center gap-1.5 rounded-lg border bg-background px-2 py-2.5 text-xs font-medium transition-all',
                      selected
                        ? `${meta.bg} ring-2 shadow-sm`
                        : 'border-input hover:bg-muted/60',
                    ].join(' ')}
                  >
                    <Icon
                      className={[
                        'h-4 w-4',
                        selected ? meta.iconColor : 'text-muted-foreground',
                      ].join(' ')}
                      aria-hidden
                    />
                    <span>{meta.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label
              htmlFor="new-issue-title"
              className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              <CircleDot className="h-3.5 w-3.5" aria-hidden /> Summary
            </Label>
            <div className="relative">
              <SelectedTypeIcon
                className={[
                  'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2',
                  TYPE_META[type].iconColor,
                ].join(' ')}
                aria-hidden
              />
              <Input
                id="new-issue-title"
                required
                maxLength={200}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
                placeholder="What needs to be done?"
                className="h-11 pl-9 text-sm"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] tabular-nums text-muted-foreground">
                {title.length}/200
              </span>
            </div>
          </div>

          {/* Parent (subtask only) */}
          {type === 'SUBTASK' ? (
            <div className="space-y-2">
              <Label
                htmlFor="new-issue-parent"
                className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <GitBranch className="h-3.5 w-3.5" aria-hidden /> Parent issue
              </Label>
              <Select
                id="new-issue-parent"
                required
                value={parentKey}
                onChange={(e) => setParentKey(e.target.value)}
                className="h-11 text-sm"
              >
                <option value="">
                  {parentsQuery.isLoading ? 'Loading…' : 'Choose a parent issue…'}
                </option>
                {parentOptions.map((issue) => (
                  <option key={issue.id} value={issue.id}>
                    {issue.key} — {issue.title}
                  </option>
                ))}
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Only Story / Task / Bug issues in this project can be parents.
              </p>
            </div>
          ) : null}

          <div className="grid gap-5 sm:grid-cols-2">
            {/* Priority */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Flame className="h-3.5 w-3.5" aria-hidden /> Priority
              </Label>
              <div className="flex items-center gap-1 rounded-lg border bg-muted/30 p-1">
                {PRIORITY_OPTIONS.map((p) => {
                  const meta = PRIORITY_META[p];
                  const Icon = meta.icon;
                  const selected = p === priority;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPriority(p)}
                      aria-pressed={selected}
                      title={meta.label}
                      aria-label={meta.label}
                      className={[
                        'flex h-8 flex-1 items-center justify-center rounded-md transition-all',
                        selected ? 'bg-background shadow-sm ring-1 ring-border' : 'hover:bg-background/60',
                      ].join(' ')}
                    >
                      <Icon
                        className={[
                          'h-4 w-4',
                          selected ? meta.color : 'text-muted-foreground',
                        ].join(' ')}
                        aria-hidden
                      />
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Selected:{' '}
                <span className="font-medium text-foreground">{PRIORITY_META[priority].label}</span>
              </p>
            </div>

            {/* Story points stepper */}
            <div className="space-y-2">
              <Label
                htmlFor="new-issue-sp"
                className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <Hash className="h-3.5 w-3.5" aria-hidden /> Story points
              </Label>
              <div className="flex h-10 items-stretch overflow-hidden rounded-lg border">
                <button
                  type="button"
                  onClick={() => bumpPoints(-1)}
                  className="flex w-10 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Decrease story points"
                >
                  <Minus className="h-4 w-4" aria-hidden />
                </button>
                <input
                  id="new-issue-sp"
                  type="number"
                  min={0}
                  max={999}
                  value={storyPoints}
                  onChange={(e) => setStoryPoints(e.target.value)}
                  placeholder="—"
                  className="w-full min-w-0 border-x border-input bg-background text-center text-sm font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring"
                />
                <button
                  type="button"
                  onClick={() => bumpPoints(1)}
                  className="flex w-10 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Increase story points"
                >
                  <Plus className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label
              htmlFor="new-issue-desc"
              className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              <FileText className="h-3.5 w-3.5" aria-hidden /> Description
            </Label>
            <div className="relative rounded-lg border focus-within:ring-2 focus-within:ring-ring">
              <textarea
                id="new-issue-desc"
                className="min-h-[120px] w-full resize-y rounded-lg bg-background p-3 pr-16 text-sm focus:outline-none"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={10_000}
                placeholder="Add more context… Markdown supported."
              />
              <span className="pointer-events-none absolute bottom-2 right-3 text-[10px] tabular-nums text-muted-foreground">
                {description.length}/10000
              </span>
            </div>
          </div>

          <DialogFooter className="pt-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || !projectId || !title.trim()}
              className="gap-1.5"
            >
              {mutation.isPending ? (
                <>
                  <CircleDashed className="h-4 w-4 animate-spin" aria-hidden /> Creating…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" aria-hidden /> Create issue
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
